
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Customer } from "@/types/crm";

interface CustomerSelectionFieldProps {
  value?: string;
  onChange: (customerId: string | undefined) => void;
}

const CustomerSelectionField = ({ value, onChange }: CustomerSelectionFieldProps) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  const fetchCustomers = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setCustomers(data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast({
        title: "Error",
        description: "Failed to fetch customers",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleCreateCustomer = async () => {
    if (!newCustomerName.trim()) {
      toast({
        title: "Error",
        description: "Customer name is required",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      const { data, error } = await supabase
        .from('customers')
        .insert([{
          name: newCustomerName.trim(),
          status: 'prospect'
        }])
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Success",
        description: "Customer created successfully",
      });

      setNewCustomerName("");
      setIsDialogOpen(false);
      fetchCustomers();
      
      // Auto-select the newly created customer
      if (data) {
        onChange(data.id);
      }
    } catch (error) {
      console.error('Error creating customer:', error);
      toast({
        title: "Error",
        description: "Failed to create customer",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const selectedCustomer = customers.find(c => c.id === value);

  return (
    <div className="space-y-2">
      <Label>Contract Seller (Customer)</Label>
      <div className="flex gap-2">
        <Select value={value || "none"} onValueChange={(val) => onChange(val === "none" ? undefined : val)}>
          <SelectTrigger className="flex-1">
            <SelectValue placeholder="Select the seller/customer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No customer selected</SelectItem>
            {customers.map((customer) => (
              <SelectItem key={customer.id} value={customer.id}>
                {customer.name} {customer.company && `(${customer.company})`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button type="button" variant="outline" size="icon">
              <Plus className="h-4 w-4" />
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Customer</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="customerName">Customer Name *</Label>
                <Input
                  id="customerName"
                  value={newCustomerName}
                  onChange={(e) => setNewCustomerName(e.target.value)}
                  placeholder="Enter customer name"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false);
                    setNewCustomerName("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleCreateCustomer}
                  disabled={isCreating}
                >
                  {isCreating ? "Creating..." : "Create Customer"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      
      {selectedCustomer && (
        <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
          <strong>{selectedCustomer.name}</strong>
          {selectedCustomer.email && <div>Email: {selectedCustomer.email}</div>}
          {selectedCustomer.phone && <div>Phone: {selectedCustomer.phone}</div>}
          {selectedCustomer.company && <div>Company: {selectedCustomer.company}</div>}
        </div>
      )}
    </div>
  );
};

export default CustomerSelectionField;
